import DesktopWindowsIcon from '@mui/icons-material/DesktopWindows';
import DownloadIcon from '@mui/icons-material/Download';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import SlideshowIcon from '@mui/icons-material/Slideshow';
import TabletIcon from '@mui/icons-material/Tablet';
import TvIcon from '@mui/icons-material/Tv';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Drawer,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Slider,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import React from 'react';

import {
  ChromeTheme,
  DeviceType,
  Orientation,
  SITE_CONTEXT_CATEGORIES,
  SiteCategory,
  SiteContextType,
} from '../../types/previewStudio';

const DRAWER_WIDTH = 280;

interface GalleryOptionsPanelProps {
  open: boolean;
  onClose: () => void;
  deviceType: DeviceType | null;
  siteContext: SiteContextType;
  orientation: Orientation;
  chromeTheme: ChromeTheme;
  showSizeLabel: boolean;
  showNameLabel: boolean;
  zoomScale: number;
  sortBy: 'name' | 'size' | 'type';
  selectedCount: number;
  totalCount: number;
  onDeviceTypeChange: (dt: DeviceType) => void;
  onSiteContextChange: (sc: SiteContextType) => void;
  onOrientationChange: (o: Orientation) => void;
  onChromeThemeChange: (t: ChromeTheme) => void;
  onShowSizeLabelChange: (v: boolean) => void;
  onShowNameLabelChange: (v: boolean) => void;
  onZoomScaleChange: (s: number) => void;
  onSortByChange: (s: 'name' | 'size' | 'type') => void;
  onSelectAll: () => void;
  onSelectNone: () => void;
  onDownloadPNG: () => void;
  onDownloadPPTX: () => void;
  isExporting: boolean;
}

export default function GalleryOptionsPanel({
  open,
  onClose, // eslint-disable-line @typescript-eslint/no-unused-vars
  deviceType,
  siteContext,
  orientation,
  chromeTheme,
  showSizeLabel,
  showNameLabel,
  zoomScale,
  sortBy,
  selectedCount,
  totalCount,
  onDeviceTypeChange,
  onSiteContextChange,
  onOrientationChange,
  onChromeThemeChange,
  onShowSizeLabelChange,
  onShowNameLabelChange,
  onZoomScaleChange,
  onSortByChange,
  onSelectAll,
  onSelectNone,
  onDownloadPNG,
  onDownloadPPTX,
  isExporting,
}: GalleryOptionsPanelProps) {
  // Derive current category from the selected site context
  const currentCategory: SiteCategory = React.useMemo(() => {
    const found = Object.entries(SITE_CONTEXT_CATEGORIES).find(
      ([, config]) => config.styles.some((s) => s.value === siteContext),
    );
    return (found ? found[0] : 'blank') as SiteCategory;
  }, [siteContext]);

  return (
    <Drawer
      variant="persistent"
      anchor="right"
      open={open}
      sx={{
        width: open ? DRAWER_WIDTH : 0,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: DRAWER_WIDTH,
          boxSizing: 'border-box',
          position: 'relative',
          height: '100%',
          border: 'none',
          borderLeft: '1px solid',
          borderColor: 'divider',
        },
      }}
    >
      <Box sx={{ p: 2, overflow: 'auto' }}>
        {/* Device Type (sets all creatives) */}
        <Typography variant="overline" sx={{ color: 'text.secondary', mb: 0.5, display: 'block' }}>
          Device {!deviceType && '(mixed)'}
        </Typography>
        <ToggleButtonGroup
          value={deviceType ?? ''}
          exclusive
          onChange={(_, v) => v && onDeviceTypeChange(v)}
          size="small"
          fullWidth
          sx={{ mb: 2 }}
        >
          <ToggleButton value="mobile"><PhoneIphoneIcon fontSize="small" /></ToggleButton>
          <ToggleButton value="tablet"><TabletIcon fontSize="small" /></ToggleButton>
          <ToggleButton value="desktop"><DesktopWindowsIcon fontSize="small" /></ToggleButton>
          <ToggleButton value="ctv"><TvIcon fontSize="small" /></ToggleButton>
        </ToggleButtonGroup>

        {/* Site Category */}
        <FormControl fullWidth size="small" sx={{ mb: 1 }}>
          <InputLabel>Category</InputLabel>
          <Select
            value={currentCategory}
            label="Category"
            onChange={(e) => {
              const cat = e.target.value as SiteCategory;
              // Auto-select first style in the new category
              const firstStyle = SITE_CONTEXT_CATEGORIES[cat].styles[0].value;
              onSiteContextChange(firstStyle);
            }}
          >
            {(Object.keys(SITE_CONTEXT_CATEGORIES) as SiteCategory[]).map((cat) => (
              <MenuItem key={cat} value={cat}>{SITE_CONTEXT_CATEGORIES[cat].label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Site Style */}
        {currentCategory !== 'blank' && (
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Style</InputLabel>
            <Select
              value={siteContext}
              label="Style"
              onChange={(e) => onSiteContextChange(e.target.value as SiteContextType)}
            >
              {SITE_CONTEXT_CATEGORIES[currentCategory].styles.map((style) => (
                <MenuItem key={style.value} value={style.value}>{style.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* Orientation */}
        <ToggleButtonGroup
          value={orientation}
          exclusive
          onChange={(_, v) => v && onOrientationChange(v)}
          size="small"
          fullWidth
          sx={{ mb: 2 }}
        >
          <ToggleButton value="portrait">Portrait</ToggleButton>
          <ToggleButton value="landscape">Landscape</ToggleButton>
        </ToggleButtonGroup>

        {/* Theme */}
        <ToggleButtonGroup
          value={chromeTheme}
          exclusive
          onChange={(_, v) => v && onChromeThemeChange(v)}
          size="small"
          fullWidth
          sx={{ mb: 2 }}
        >
          <ToggleButton value="light">Light</ToggleButton>
          <ToggleButton value="dark">Dark</ToggleButton>
        </ToggleButtonGroup>

        <Divider sx={{ my: 2 }} />

        {/* Overlays */}
        <Typography variant="overline" sx={{ color: 'text.secondary', mb: 0.5, display: 'block' }}>
          Overlays
        </Typography>
        <FormControlLabel
          control={<Switch size="small" checked={showSizeLabel} onChange={(_, v) => onShowSizeLabelChange(v)} />}
          label={<Typography variant="body2">Ad size label</Typography>}
          sx={{ mb: 0.5, ml: 0 }}
        />
        <FormControlLabel
          control={<Switch size="small" checked={showNameLabel} onChange={(_, v) => onShowNameLabelChange(v)} />}
          label={<Typography variant="body2">Creative name</Typography>}
          sx={{ mb: 1, ml: 0 }}
        />

        {/* Zoom */}
        <Typography variant="overline" sx={{ color: 'text.secondary', mb: 0.5, display: 'block' }}>
          Zoom ({Math.round(zoomScale * 100)}%)
        </Typography>
        <Slider
          value={zoomScale}
          onChange={(_, v) => onZoomScaleChange(v as number)}
          min={0.25}
          max={1.5}
          step={0.05}
          size="small"
          sx={{ mb: 2 }}
        />

        {/* Sort */}
        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel>Sort by</InputLabel>
          <Select
            value={sortBy}
            label="Sort by"
            onChange={(e) => onSortByChange(e.target.value as 'name' | 'size' | 'type')}
          >
            <MenuItem value="name">Name</MenuItem>
            <MenuItem value="size">Size</MenuItem>
            <MenuItem value="type">Type</MenuItem>
          </Select>
        </FormControl>

        <Divider sx={{ my: 2 }} />

        {/* Selection */}
        <Typography variant="overline" sx={{ color: 'text.secondary', mb: 0.5, display: 'block' }}>
          Selection ({selectedCount} of {totalCount})
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <Button size="small" variant="outlined" onClick={onSelectAll} fullWidth>
            Select All
          </Button>
          <Button size="small" variant="outlined" onClick={onSelectNone} fullWidth>
            None
          </Button>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Download */}
        <Typography variant="overline" sx={{ color: 'text.secondary', mb: 1, display: 'block' }}>
          Download
        </Typography>
        <Button
          variant="contained"
          size="small"
          fullWidth
          startIcon={isExporting ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
          onClick={onDownloadPNG}
          disabled={selectedCount === 0 || isExporting}
          sx={{ mb: 1 }}
        >
          {selectedCount > 1 ? `Download ${selectedCount} PNGs (ZIP)` : 'Download PNG'}
        </Button>
        <Button
          variant="contained"
          size="small"
          fullWidth
          startIcon={isExporting ? <CircularProgress size={16} color="inherit" /> : <SlideshowIcon />}
          onClick={onDownloadPPTX}
          disabled={selectedCount === 0 || isExporting}
          color="secondary"
        >
          Download PPTX
        </Button>

      </Box>
    </Drawer>
  );
}

export { DRAWER_WIDTH };
